'use client'

import posthog from 'posthog-js'
import { PostHogProvider as PHProvider, usePostHog } from 'posthog-js/react'
import { Suspense } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect } from 'react'
import { env } from '@/env.mjs'
import { redactTokenUrlsInPostHogEvent } from '@/lib/redact/token-url'

// Initialize PostHog synchronously (before React renders) for immediate bootstrap values
const isLocal = typeof window !== 'undefined' && env.NEXT_PUBLIC_APP_ENV === 'local'
const isInitialized =
  typeof window !== 'undefined' &&
  !isLocal &&
  !posthog.__loaded &&
  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY!, {
    api_host: '/sj-ph',
    ui_host: 'https://us.posthog.com',
    capture_pageview: false, // We capture pageviews manually
    capture_pageleave: true, // Enable pageleave capture
    capture_exceptions: true, // Capture uncaught errors + unhandled promise rejections
    persistence: 'localStorage+cookie',
    // Public pages that carry their credential in the URL (the my-tickets signed
    // link, the door check-in link, the unsubscribe page) must not hand it to
    // PostHog in `$current_url` / `$pathname` / `$referrer` (SCH-3420).
    before_send: redactTokenUrlsInPostHogEvent,
    bootstrap: {
      featureFlags: {
        // Marketing nav defaults - show these pages by default
        parents_page: true,
        schools_page: true,
        features_page: true,
        pricing_page: true,
        start_page: true,
        blog_page: false // Hidden until development complete
      }
    }
  })

if (isLocal && typeof window !== 'undefined') {
  console.log('PostHog disabled in local environment')
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  return (
    <PHProvider client={posthog}>
      <SuspendedPostHogPageView />
      {children}
    </PHProvider>
  )
}

function PostHogPageView() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const posthog = usePostHog()

  useEffect(() => {
    if (pathname && posthog) {
      let url = window.origin + pathname
      const search = searchParams.toString()
      if (search) {
        url += '?' + search
      }
      posthog.capture('$pageview', { $current_url: url })
    }
  }, [pathname, searchParams, posthog])

  return null
}

function SuspendedPostHogPageView() {
  return (
    <Suspense fallback={null}>
      <PostHogPageView />
    </Suspense>
  )
}
